/**
 * Dexie DB 'kgr-offline' (listed in wipe.ts LOCAL_DB_NAMES, so it is deleted on logout / user switch)
 * and thin Cache API helpers for 'kgr-content-v1' (packs + avatars) and 'kgr-recent-v1' (recent LRU).
 */
import Dexie, { type Table } from 'dexie';
import type { SheetTier } from '../../shared/dto/library';
import type { ManifestDTO } from '../../shared/dto/offline';
import { CONTENT_CACHE, OFFLINE_DB_NAME, RECENT_CACHE, type ItemRow, type PackRow, type RecentEntry } from './types';
import { withReopen } from '../lib/idb';

export interface MetaMap {
  manifest: ManifestDTO;
  tier: SheetTier;
  recent: RecentEntry[];
  /** Avatar URLs stored in the content cache. */
  avatars: string[];
  /**
   * Sheets whose local annotation copies must still be removed (they left the manifest). Written in
   * the same transaction as the new manifest and cleared only once the purge succeeded, so an
   * interrupted purge is retried on the next sync.
   */
  pendingPurge: string[];
}
export type MetaKey = keyof MetaMap;

interface MetaRow {
  k: string;
  v: unknown;
}

export class OfflineDb extends Dexie {
  packs!: Table<PackRow, string>;
  items!: Table<ItemRow, string>;
  meta!: Table<MetaRow, string>;

  constructor() {
    super(OFFLINE_DB_NAME);
    this.version(1).stores({
      packs: 'id, kind, status',
      items: 'url, fileId, songId, *packIds',
      meta: 'k',
    });
  }
}

let instance: OfflineDb | null = null;

export function offlineDb(): OfflineDb {
  // A connection closed by a deletion elsewhere (versionchange) cannot be reused: start a new one.
  if (!instance || (!instance.isOpen() && instance.hasBeenClosed())) instance = new OfflineDb();
  return instance;
}

/** Closes the connection so the database can be deleted (wipe). */
export function closeOfflineDb(): void {
  if (instance) {
    instance.close();
    instance = null;
  }
}

/** Runs `op` on the offline DB; a connection Safari dropped is reopened and `op` runs once more (lib/idb.ts). */
export function offlineOp<T>(op: (db: OfflineDb) => Promise<T>): Promise<T> {
  const db = offlineDb();
  return withReopen(db, () => op(db));
}

export async function getMeta<K extends MetaKey>(k: K): Promise<MetaMap[K] | undefined> {
  const row = await offlineOp((db) => db.meta.get(k));
  return row?.v as MetaMap[K] | undefined;
}

export async function setMeta<K extends MetaKey>(k: K, v: MetaMap[K]): Promise<void> {
  await offlineOp((db) => db.meta.put({ k, v }));
}

/* ─── Cache API ─────────────────────────────────────────────────────────── */

export function hasCacheApi(): boolean {
  return typeof caches !== 'undefined';
}

export async function openContent(): Promise<Cache | null> {
  if (!hasCacheApi()) return null;
  try {
    return await caches.open(CONTENT_CACHE);
  } catch {
    return null;
  }
}

export async function openRecent(): Promise<Cache | null> {
  if (!hasCacheApi()) return null;
  try {
    return await caches.open(RECENT_CACHE);
  } catch {
    return null;
  }
}

export async function deleteFromCache(cache: Cache | null, urls: Iterable<string>): Promise<void> {
  if (!cache) return;
  await Promise.all([...urls].map((u) => cache.delete(u).catch(() => false)));
}
